import React, { createContext, useContext, useState } from "react";

import api from "@/services/api";

interface LoginData {
  cpf: string;
  birthDate: string;
  company?: {
    id: string;
  };
  recaptcha_token?: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
}

interface LoginResponse {
  id: string;
  user: User;
  role: string;
  accessToken: string;
  company?: {
    id: string;
    name: string;
  };
  shippingCompanyId?: string;
  useTerms?: boolean;
}

interface AuthContextData {
  user: User | null;
  driver: User | null;
  isAuthenticated: boolean;
  isDriver: boolean;
  isShipping: boolean;
  isMenuVisible: boolean;
  Login: (data: object) => Promise<LoginResponse>;
  Logout: () => void;
  driverLogin: (data: LoginData) => Promise<boolean>;
  driverLogout: () => void;
  toggleMenu: () => void;
  company: string | null;
  role: string | null;
  LogoutExped: () => void;
  setMenuExpand: () => void;
  setMenuExpandFalse: () => void;
  menu: boolean;
  driverSigned: () => boolean;
}

const AuthContext = createContext<AuthContextData>({} as AuthContextData);

function AuthProvider({ children }: { children: React.ReactNode }) {
  // const navigate = useNavigate();

  const [user, setUser] = useState<User | null>(() => {
    const storedUser = sessionStorage.getItem("@App:user");
    return storedUser ? JSON.parse(JSON.stringify(storedUser)) : null;
  });

  const [driver, setDriver] = useState<User | null>(() => {
    const storedDriver = { name: sessionStorage.getItem("@AppDriver:driver")! };
    return storedDriver ? JSON.parse(JSON.stringify(storedDriver)) : null;
  });

  const [isMenuVisible, setIsMenuVisible] = useState(() => {
    const storedMenuState = localStorage.getItem("@App:menuVisible");
    return storedMenuState ? JSON.parse(storedMenuState) : false;
  });

  const [company, setCompany] = useState<string | null>(() => {
    return localStorage.getItem("@App:companyId") || null;
  });
  const [role, setRole] = useState<string | null>(() => {
    return sessionStorage.getItem("@App:userRole") || null;
  });
  const [menu, setMenu] = useState<boolean>(false);

  const isAuthenticated = !!user;
  const isDriver = !!driver;

  const getRole = (role: string) => {
    const isShipping = role === "Transportadora";
    return isShipping;
  };

  const isShipping = user ? getRole(user.role) : false;

  const Login = async (userData: object): Promise<LoginResponse> => {
    return await api.post("auth/login", userData).then((response) => {
      setUser(response.data.user);
      setRole(response.data.role);
      setCompany(response.data.company?.id);

      api.defaults.headers.common["Authorization"] = `Bearer ${response.data.accessToken}`;

      sessionStorage.setItem("@App:id", response.data.id);
      sessionStorage.setItem("@App:user", response.data.user);
      sessionStorage.setItem("@App:token", response.data.accessToken);
      sessionStorage.setItem("@App:userRole", response.data.role);

      if (response.data.company) {
        localStorage.setItem("@App:companyId", response.data.company?.id);
        sessionStorage.setItem("@App:companyName", response.data.company?.name);
      }

      if (response.data.shippingCompanyId) {
        localStorage.setItem("@App:shippingCompanyId", response.data?.shippingCompanyId);
      }

      if (response.data.role === "Transportadora" && response.data.useTerms === false) {
        sessionStorage.setItem("@App:temporaryCompanyId", response.data.company?.id);
        sessionStorage.setItem("@App:temporaryId", response.data.id);
      }

      return response.data;
    });
  };

  const Logout = () => {
    setUser(null);
    setRole(null);
    setCompany(null);
    localStorage.removeItem("@App:user");
    localStorage.removeItem("@App:token");
    localStorage.removeItem("@App:companyId");
    sessionStorage.removeItem("@App:userRole");
    delete api.defaults.headers.common["Authorization"];
  };

  const driverLogin = async (driverData: object): Promise<boolean> => {
    sessionStorage.removeItem("@App:id");

    return await api.post("auth/app/login", driverData).then((response) => {
      if (response.status === 201) {
        setDriver(response.data.name);
        api.defaults.headers.common["Authorization"] = `Bearer ${response.data.accessToken}`;

        sessionStorage.setItem("@AppDriver:id", JSON.stringify(response.data.id));
        sessionStorage.setItem("@AppDriver:driver", JSON.stringify(response.data.name));
        sessionStorage.setItem("@AppDriver:token", response.data.accessToken);
        sessionStorage.setItem("@AppDriver:companyId", response.data.company?.id);
        sessionStorage.setItem("@AppDriver:companyName", response.data.company?.name);

        if (response.data.useTerms === false) {
          sessionStorage.setItem("@App:temporaryCompanyId", response.data.company?.id);
          sessionStorage.setItem("@App:temporaryDriverId", response.data.id);
        }
        return true;
      }
      return false;
    });
  };

  const driverLogout = () => {
    setDriver(null);
    localStorage.removeItem("@AppDriver:driver");
    localStorage.removeItem("@AppDriver:driverToken");
    delete api.defaults.headers.common["Authorization"];

    localStorage.clear();
    sessionStorage.clear();
    // navigate({ to: "/app/login", replace: true });
  };

  const LogoutExped = () => {
    Logout();
  };

  const setMenuExpand = () => {
    setMenu(true);
  };
  const setMenuExpandFalse = () => {
    setMenu(false);
  };
  const toggleMenu = () => {
    const newMenuState = !isMenuVisible;
    setIsMenuVisible(newMenuState);
    localStorage.setItem("@App:menuVisible", JSON.stringify(newMenuState));
  };

  const driverSigned = () => {
    return sessionStorage.getItem("@AppDriver:driver") ? true : false;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        driver,
        isAuthenticated,
        isDriver,
        isShipping,
        isMenuVisible,
        Login,
        Logout,
        driverLogin,
        driverLogout,
        driverSigned,
        toggleMenu,
        company,
        role,
        LogoutExped,
        setMenuExpand,
        setMenuExpandFalse,
        menu
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export { AuthProvider, useAuth };
